import { ref, watch, onMounted } from 'vue';

/**
 * Composable para auto-save do formulário no localStorage
 * Salva automaticamente os dados do formulário com debounce
 */
export function useFormAutoSave(slug, formData, options = {}) {
  const {
    debounceDelay = 1000, // 1 segundo de delay
    storagePrefix = 'rsvp_form_',
    enabled = true
  } = options;

  const storageKey = `${storagePrefix}${slug}`;
  const lastSaved = ref(null);
  const isSaving = ref(false);
  let saveTimeout = null;

  /**
   * Salva os dados no localStorage
   */
  const save = () => {
    if (!enabled) return;

    try {
      isSaving.value = true;
      const dataToSave = {
        formData: formData.value,
        timestamp: new Date().toISOString(),
        slug
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      lastSaved.value = new Date();
    } catch (err) {
      console.error('Erro ao salvar formulário no localStorage:', err);
    } finally {
      isSaving.value = false;
    }
  };

  /**
   * Salva com debounce
   */
  const debouncedSave = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      save();
    }, debounceDelay);
  };

  /**
   * Restaura os dados do localStorage
   */
  const restore = () => {
    if (!enabled) return null;

    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;

      const parsed = JSON.parse(saved);

      // Valida que é o formulário correto
      if (parsed.slug !== slug) {
        console.warn('Slug do formulário salvo não corresponde ao atual');
        return null;
      }

      lastSaved.value = new Date(parsed.timestamp);
      return parsed.formData;
    } catch (err) {
      console.error('Erro ao restaurar formulário do localStorage:', err);
      return null;
    }
  };

  /**
   * Limpa os dados salvos
   */
  const clear = () => {
    try {
      localStorage.removeItem(storageKey);
      lastSaved.value = null;
    } catch (err) {
      console.error('Erro ao limpar formulário do localStorage:', err);
    }
  };

  /**
   * Verifica se existe dados salvos
   */
  const hasSavedData = () => {
    if (!enabled) return false;
    return localStorage.getItem(storageKey) !== null;
  };

  /**
   * Retorna informações sobre o último save
   */
  const getSavedInfo = () => {
    if (!enabled) return null;

    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;

      const parsed = JSON.parse(saved);
      return {
        timestamp: parsed.timestamp,
        slug: parsed.slug,
        fieldsCount: Object.keys(parsed.formData).length
      };
    } catch (err) {
      return null;
    }
  };

  // Watch formData para auto-save
  if (enabled) {
    watch(
      () => formData.value,
      () => {
        debouncedSave();
      },
      { deep: true }
    );
  }

  // Restaura dados ao montar
  onMounted(() => {
    if (enabled && hasSavedData()) {
      const savedData = restore();
      if (savedData) {
        // Merge saved data com formData atual (sem sobrescrever valores já preenchidos)
        Object.keys(savedData).forEach(key => {
          if (formData.value[key] === undefined || formData.value[key] === null || formData.value[key] === '') {
            formData.value[key] = savedData[key];
          }
        });
      }
    }
  });

  return {
    // State
    lastSaved,
    isSaving,

    // Methods
    save,
    restore,
    clear,
    hasSavedData,
    getSavedInfo
  };
}
